/**
 * Invite a friend, from the phone.
 *
 * Admin only. The invite link is the only thing that matters here, so the UI is
 * built around getting it onto the clipboard in one tap, ready to paste into a
 * chat.
 *
 * Copy is the primary action, deliberately. The native share sheet is a nice
 * extra but it hijacks the flow — it makes you pick an app up front, when what
 * you usually want is the link in hand so you can paste it wherever you happen
 * to be talking to the person.
 *
 * Copy has a real fallback chain, because clipboard access fails more often
 * than people expect in a WebView:
 *   1. navigator.clipboard.writeText — needs a secure context and a user
 *      gesture. https://localhost in the APK qualifies, so this is the path
 *      almost every time.
 *   2. A hidden textarea + document.execCommand('copy') — deprecated, still
 *      works where the modern API is blocked.
 *   3. Select the visible field so it can be long-pressed and copied by hand,
 *      and say so.
 */

import { useEffect, useRef, useState } from 'react'

import { api, ApiError } from '../api/client'
import { parseIso } from '../lib/time'

type Invite = {
  id: number
  url: string
  created_at: string | null
  expires_at: string | null
  used_at: string | null
  used_by: string | null
  is_valid: boolean
}

async function copyText(text: string, field: HTMLInputElement | null): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* blocked — fall through */
  }

  try {
    const ta = document.createElement('textarea')
    ta.value = text
    // Off-screen but focusable. display:none would make the copy a no-op.
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    ta.style.top = '0'
    document.body.appendChild(ta)
    ta.focus()
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    if (ok) return true
  } catch {
    /* fall through to manual selection */
  }

  field?.focus()
  field?.select()
  return false
}

export function InviteFriend() {
  const [invites, setInvites] = useState<Invite[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const fields = useRef<Record<number, HTMLInputElement | null>>({})

  const load = () =>
    api<{ invites: Invite[] }>('/invites')
      .then((r) => setInvites(r.invites))
      .catch(() => setInvites(null))

  useEffect(() => { void load() }, [])

  const unused = (invites ?? []).filter((i) => i.is_valid && !i.used_at)
  const canShare = typeof navigator !== 'undefined' && 'share' in navigator

  return (
    <>
      <h2>Invite a friend</h2>
      <div className="card">
        {error && <div className="error">{error}</div>}

        {unused.length > 0 ? (
          unused.map((invite) => (
            <div key={invite.id} style={{ marginBottom: '1rem' }}>
              <input
                ref={(el) => { fields.current[invite.id] = el }}
                readOnly
                value={invite.url}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                style={{ fontSize: '.8rem', fontFamily: 'ui-monospace, monospace' }}
              />

              <button
                style={{ width: '100%' }}
                onClick={async () => {
                  setError(null)
                  const ok = await copyText(invite.url, fields.current[invite.id])
                  if (ok) {
                    setCopiedId(invite.id)
                    setTimeout(
                      () => setCopiedId((c) => (c === invite.id ? null : c)),
                      2500,
                    )
                  } else {
                    setError("Couldn't copy automatically — the link is selected, "
                             + 'long-press it and choose Copy.')
                  }
                }}
              >
                {copiedId === invite.id ? '✓ Copied — paste it in a chat' : 'Copy link'}
              </button>

              <div style={{ display: 'flex', gap: '.5rem' }}>
                {canShare && (
                  <button
                    className="secondary"
                    style={{ flex: 1 }}
                    onClick={async () => {
                      try {
                        await navigator.share({
                          title: 'Lift invite',
                          text: `Come train with me on Lift.\n\n${invite.url}\n\n`
                                + 'That link sets up your account and shows you '
                                + 'how to install the app.',
                        })
                      } catch {
                        /* dismissed — not an error worth showing */
                      }
                    }}
                  >
                    Share…
                  </button>
                )}
                <button
                  className="secondary"
                  style={{ flex: 1 }}
                  onClick={async () => {
                    await api(`/invites/${invite.id}`, { method: 'DELETE' })
                    void load()
                  }}
                >
                  Cancel
                </button>
              </div>

              <div className="muted" style={{ fontSize: '.8rem', marginTop: '.4rem' }}>
                Works once · expires {invite.expires_at
                  ? parseIso(invite.expires_at).toLocaleDateString(
                      [], { month: 'short', day: 'numeric' })
                  : 'never'}
              </div>
            </div>
          ))
        ) : (
          <p className="muted" style={{ marginTop: 0 }}>
            {invites === null
              ? 'Needs a connection.'
              : 'No unused invites yet.'}
          </p>
        )}

        <button
          className={unused.length > 0 ? 'secondary' : ''}
          disabled={busy}
          style={{ width: '100%' }}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              await api<{ invite: Invite }>('/invites', { body: { days: 14 } })
              await load()
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Could not create an invite.')
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Creating…' : 'New invite link'}
        </button>

        <p className="muted" style={{ fontSize: '.8rem', marginBottom: 0 }}>
          One link, one person. It walks them through creating an account and
          installing the app.
        </p>
      </div>

      {(invites ?? []).some((i) => i.used_at) && (
        <div className="card" style={{ marginTop: '.75rem' }}>
          <strong style={{ fontSize: '.9rem' }}>Already joined</strong>
          <ul className="plain" style={{ margin: '.3rem 0 0' }}>
            {(invites ?? [])
              .filter((i) => i.used_at)
              .slice(0, 8)
              .map((i) => (
                <li key={i.id}>
                  <span>{i.used_by ?? 'someone'}</span>
                  <span className="muted">
                    {i.used_at &&
                      parseIso(i.used_at).toLocaleDateString(
                        [], { month: 'short', day: 'numeric' })}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </>
  )
}
